import _, { result } from "lodash";

/**
 * Finds the appropriate Lumen result from the given run
 * Can search for application-level or applicant-specific results
 */
export function findLumenResult(lumenRun, applicant = undefined) {
  return (
    _.find(
      lumenRun?.results,
      applicant
        ? (result) => _.includes(result?.applicant, applicant)
        : "application"
    ) || {}
  );
}

/**
 * Search through evaluation history for entries matching a query
 */
export function queryEvaluationHistory(history, query, key = undefined) {
  const entry = _.find(history, (entry) => _.startsWith(entry?.formula, query));
  return key ? _.get(entry, key) : entry;
}

/**
 * Flexible history item search that can match by starts with, contains, or exact match
 * @param lumenResult - The Lumen result containing evaluation history
 * @param formulaName - The formula name to search for
 * @param options - Configuration options for the search
 * @returns A single matching history item or null
 */
export function findHistoryItem(
  lumenResult,
  formulaName,
  options = { matchType: "contains", returnAll: false, key: undefined }
) {
  if (!lumenResult || !formulaName) {
    return options.returnAll ? [] : null;
  }

  // Get all history items
  const historyItems = _.get(lumenResult, "evaluation.history", []);

  // Normalize the search term
  const normalizedSearch = formulaName.toLowerCase().trim();

  // Find items with matching formula based on match type
  const matchFn = (item) => {
    if (!item.formula) return false;
    const normalizedFormula = item.formula.toLowerCase();

    switch (options.matchType) {
      case "startsWith":
        return normalizedFormula.startsWith(normalizedSearch);
      case "exact":
        return normalizedFormula === normalizedSearch;
      case "contains":
      default:
        return normalizedFormula.includes(normalizedSearch);
    }
  };

  // Return all matches or just the first one
  if (options.returnAll) {
    const matches = historyItems.filter(matchFn);
    return options.key
      ? matches.map((item) => _.get(item, options.key))
      : matches;
  }

  const match = historyItems.find(matchFn);
  return options.key ? _.get(match, options.key) : match;
}

/**
 * Query evaluation results using annotation markers
 * Useful for finding specific calculated values in the evaluation history
 */
export function queryByAnnotation(result, annotations, key: any = "result") {
  return _.chain(annotations)
    .castArray()
    .map((annotation) =>
      queryEvaluationHistory(
        result?.evaluation?.history,
        `#${annotation}:`,
        key
      )
    )
    .reject(_.isNil)
    .first()
    .value();
}

/**
 * Check if a background report is incomplete
 * @param {Object} lumenRun - The lumen run object
 * @returns {boolean} True if background report is incomplete
 */
export function isBackgroundReportIncomplete(lumenRun) {
  // Check if we have the required data and a failed background-reports-run outcome
  return (
    lumenRun?.outcomes?.some((outcome) => {
      const outcomeName = outcome?.name || outcome?.formula;
      return (
        outcomeName &&
        outcomeName.toLowerCase().includes("background-reports-run") &&
        outcome.result === false
      );
    }) === true
  );
}

/**
 * Generic function to match keywords in a collection of objects
 * @param collection Array of objects to search through
 * @param keywords Array of keywords to check for
 * @param options Configuration options for the search
 * @returns True if the keywords match according to the specified options
 */
export function matchKeywords(
  collection: Array<any>,
  keywords: string[],
  options = {
    property: "name", // Property to check in each object
    matchAll: true, // Whether all keywords must match
    matchWholeWords: false, // Whether to match whole words only
    caseSensitive: false, // Whether matches should be case-sensitive
    checkProperties: [] as string[], // Additional properties to check beyond the main one
  }
): boolean {
  if (
    !collection ||
    !Array.isArray(collection) ||
    collection.length === 0 ||
    !keywords ||
    !Array.isArray(keywords) ||
    keywords.length === 0
  ) {
    return false;
  }

  // Helper function to check if a keyword matches a string
  const keywordMatches = (keyword: string, text: string) => {
    if (!text) return false;

    // Create regex pattern based on options
    const pattern = options.matchWholeWords ? `\\b${keyword}\\b` : keyword;
    const flags = options.caseSensitive ? "" : "i";
    const regex = new RegExp(pattern, flags);

    return regex.test(text);
  };

  // Helper function to check if keyword matches any property in an item
  const itemContainsKeyword = (item: any, keyword: string) => {
    // Check the main property
    if (keywordMatches(keyword, item[options.property])) {
      return true;
    }

    // Check additional properties if specified
    if (options.checkProperties.length > 0) {
      return options.checkProperties.some((prop) =>
        keywordMatches(keyword, item[prop])
      );
    }

    return false;
  };

  // Determine whether to check if all keywords match or any keyword matches
  const matchFunction = options.matchAll ? keywords.every : keywords.some;

  return matchFunction.call(keywords, (keyword) => {
    return collection.some((item) => itemContainsKeyword(item, keyword));
  });
}

/**
 * Checks if all keywords are present in the outcomes array
 * @param outcomes Array of outcome objects containing a name property
 * @param keywords Array of keywords to check for
 * @returns True if all keywords are present in at least one outcome name
 */
export function matchAllKeywordsInOutcomes(
  outcomes: Array<{ name: string }>,
  keywords: string[]
): boolean {
  return matchKeywords(outcomes, keywords, {
    property: "name",
    matchAll: true,
    matchWholeWords: false,
    caseSensitive: false,
    checkProperties: [],
  });
}

/**
 * Extracts income to rent ratio and cash runway data from Lumen results
 * @param applicationLumenResult - The application Lumen result
 * @returns Object containing income to rent ratio, cash runway values and status
 */
export function getIncomeAndCashRunwayData(applicationLumenResult) {
  const data = {
    incomeToRentRatio: null,
    cashRunway: null,
    status: "unknown",
  };

  if (!applicationLumenResult || !applicationLumenResult.evaluation) {
    return data;
  }

  data.incomeToRentRatio = queryByAnnotation(
    applicationLumenResult,
    "income-to-rent"
  );
  data.cashRunway = queryByAnnotation(applicationLumenResult, "cash-runway");

  const incomeToRentIdeal = queryByAnnotation(
    applicationLumenResult,
    "income-to-rent-ideal"
  );
  const incomeToRentConditional = queryByAnnotation(
    applicationLumenResult,
    "income-to-rent-range-conditional"
  );
  const cashRunwayIdeal = queryByAnnotation(
    applicationLumenResult,
    "cash-runway-ideal"
  );
  const cashRunwayConditional = queryByAnnotation(
    applicationLumenResult,
    "cash-runway-range-conditional"
  );

  if (
    incomeToRentIdeal !== undefined ||
    incomeToRentConditional !== undefined
  ) {
    if (incomeToRentIdeal) {
      data.status = "pass";
    } else if (incomeToRentConditional) {
      data.status = "warn";
    } else {
      data.status = "fail";
    }
  }

  if (cashRunwayIdeal !== undefined || cashRunwayConditional !== undefined) {
    if (cashRunwayIdeal) {
      data.status = "pass";
    } else if (cashRunwayConditional && data.status !== "pass") {
      data.status = "warn";
    } else if (data.status === "unknown") {
      data.status = "fail";
    }
  }

  return data;
}
